import type { Locale } from "@borealis/layout-express";
import { formatNumber } from "@borealis/number-format";
import type {
  ICarLeasingAdView,
  ICarLeasingBusinessAdView,
  ICarNewAdView,
  ICarUsedAdView,
  ICarUsedBusinessAdView,
} from "@finn-no/adview-provider-frontend-types";
import type { I18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react";
import type { CarAd, MobilityAd } from "@mobility-item-page/types";
import { isCarAd } from "@mobility-item-page/utilities";
import { ElementType, Intent, TargetType } from "@mobility-item-page/utilities/client";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";

type Props = {
  ad: MobilityAd;
};

const Warranty = ({ ad }: Props) => {
  const { trackClick } = usePulse();
  const info = useInfoText(ad);
  if (info === undefined) {
    return null;
  }
  const warrantyGuideUrl = getWarrantyUrl(ad);

  return (
    <div>
      <p className="mb-0 whitespace-pre-line">{info}</p>
      {warrantyGuideUrl && (
        <p className="mt-16 md:mt-8 mb-0">
          <a
            target="_blank"
            rel="noopener nofollow"
            href={warrantyGuideUrl}
            onClick={() =>
              // Tracking event: MO_ev21
              trackClick({
                name: "Read warranty guide",
                objectId: "MO_ev21",
                elementType: ElementType.Link,
                intent: Intent.Open,
                target: {
                  id: warrantyGuideUrl,
                  type: TargetType.Article,
                  name: "Nyttig info om garanti til deg som skal handle bil på FINN",
                },
              })
            }
          >
            <Trans id="itemPageLayout.safetyElement.warranty.readmore" message="Read more about warranty types here" />
          </a>
        </p>
      )}
    </div>
  );
};

const hasWarrantyDescription = (
  ad: CarAd,
): ad is ICarUsedAdView | ICarUsedBusinessAdView | ICarLeasingAdView | ICarLeasingBusinessAdView | ICarNewAdView => {
  return !!(
    ad &&
    "warranty" in ad &&
    ad.warranty &&
    "description" in ad.warranty &&
    ad.warranty.description !== undefined
  );
};

const useInfoText = (ad: MobilityAd) => {
  const { locale } = useAppConfig();
  const { i18n } = useLingui();

  if ("warranty" in ad && ad.warranty && isCarAd(ad) && hasWarrantyDescription(ad)) {
    return ad.warranty.description;
  }

  switch (ad.adViewType) {
    case "car-new":
    case "car-used":
    case "car-used-business": {
      if (!ad?.warranty) return undefined;
      const warrantyInfo = i18n._({
        id: "itemPageLayout.safetyElement.warranty.car.warrantyInfo.sold",
        message: "This car is sold with",
        comment: "Warranty info for car sold. Used in 'This car is sold with {warrantyType}'",
      });
      return getFullWarrantyInfo(
        locale,
        warrantyInfo,
        ad.warranty?.warranty_type?.value,
        ad?.warranty?.distance,
        ad?.warranty?.duration,
        i18n,
      );
    }
    case "car-leasing":
    case "car-leasing-business": {
      if (!ad?.warranty) return undefined;
      const warrantyInfo = i18n._({
        id: "itemPageLayout.safetyElement.warranty.car.warrantyInfo.leased",
        message: "This car is leased with",
        comment: "Warranty info for car leased. Used in 'This car is leased with {warrantyType}'",
      });
      return getFullWarrantyInfo(
        locale,
        warrantyInfo,
        ad.warranty?.warranty_type?.value,
        ad?.warranty?.distance,
        ad?.warranty?.duration,
        i18n,
      );
    }

    case "motorcycle":
    case "moped":
    case "atv":
    case "snowmobile": {
      if (!ad?.warranty) return undefined;
      const warrantyInfo = i18n._({
        id: "itemPageLayout.safetyElement.warranty.mc.warrantyInfo",
        message: "This vehicle is sold with",
        comment: "Warranty info for MC. Used in 'This vehicle is sold with {warrantyType}'",
      });
      return getFullWarrantyInfo(
        locale,
        warrantyInfo,
        ad.warranty?.warranty_type,
        ad?.warranty?.distance,
        ad?.warranty?.duration,
        i18n,
      );
    }

    case "mobile-home": {
      if (!ad?.warranty) return undefined;
      const warrantyInfo = i18n._({
        id: "itemPageLayout.safetyElement.warranty.motorHome.warrantyInfo",
        message: "This motor home is sold with",
        comment: "Warranty info for motor home. Used in 'This motor home is sold with {warrantyType}'",
      });
      return getFullWarrantyInfo(
        locale,
        warrantyInfo,
        ad.warranty?.type,
        ad?.warranty?.distance,
        ad?.warranty?.duration,
        i18n,
      );
    }

    case "caravan": {
      if (!ad?.warranty) return undefined;
      const warrantyInfo = i18n._({
        id: "itemPageLayout.safetyElement.warranty.caravan.warrantyInfo",
        message: "This caravan is sold with",
        comment: "Warranty info for caravan. Used in 'This caravan is sold with {warrantyType}'",
      });
      return getFullWarrantyInfo(locale, warrantyInfo, undefined, undefined, undefined, i18n);
    }

    default:
      return undefined;
  }
};

export default Warranty;

const getFullWarrantyInfo = (
  locale: Locale,
  warrantyInfo: string,
  warrantyType: string | undefined,
  distance: number | undefined,
  duration: number | undefined,
  i18n: I18n,
) => {
  const warrantyTypePart =
    warrantyType ||
    i18n._({
      id: "itemPageLayout.safetyElement.warranty.type.fallback",
      message: "warranty",
      comment:
        "Generic warranty used as fallback when no specific warranty is provided. " +
        "E.g. 'This motor home is sold with warranty'",
    });

  const detailsPart = [
    duration &&
      duration >= 6 &&
      i18n._({
        id: "itemPageLayout.safetyElement.warranty.duration",
        message: "{duration} mo.",
        comment: "Warranty duration per month",
        values: { duration: formatNumber(duration, locale) },
      }),
    distance &&
      i18n._({
        id: "itemPageLayout.common.distance.kilometer.postfix",
        message: "{distance} km",
        comment: "Distance incl. postfix",
        values: { distance: formatNumber(distance, locale) },
      }),
  ]
    .filter(Boolean)
    .join(" / ");

  return detailsPart
    ? `${warrantyInfo} ${warrantyTypePart} (${detailsPart}).`
    : `${warrantyInfo} ${warrantyTypePart}${warrantyTypePart.endsWith(".") ? "" : "."}`;
};

const getWarrantyUrl = (ad: MobilityAd) => {
  if (isCarAd(ad)) {
    return ad.warranty?.warranty_guide_url;
  }
  return undefined;
};
